import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { auth, RecaptchaVerifier, signInWithPhoneNumber } from '../lib/firebase';
import { Phone, ArrowLeft, Loader2, CheckCircle, Shield } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

import { BACKEND_URL } from '../lib/backend';

const API = BACKEND_URL;

const describeAuthError = (err, language) => {
  const hi = language === 'hi';
  const code = err?.code || '';
  if (code === 'auth/invalid-verification-code') return hi ? 'गलत OTP। कृपया पुनः प्रयास करें।' : 'Wrong OTP. Please check the code and try again.';
  if (code === 'auth/code-expired' || code === 'auth/session-expired') return hi ? 'OTP की समय सीमा समाप्त। नया OTP भेजें।' : 'This OTP has expired. Please tap Resend to get a new one.';
  if (code === 'auth/network-request-failed') return hi ? 'नेटवर्क त्रुटि। इंटरनेट जांचें।' : 'Network error. Please check your internet and try again.';
  if (code.startsWith('auth/')) return `${hi ? 'सत्यापन विफल' : 'Verification failed'} (${code.replace('auth/', '')})`;
  if (err?.response) return `${hi ? 'सर्वर त्रुटि' : 'Server error'}: ${err.response.data?.detail || err.response.status}`;
  if (err?.request) return hi ? 'सर्वर से कनेक्ट नहीं हो पाया।' : "Can't reach the server. Please try again.";
  return hi ? 'सत्यापन विफल। पुनः प्रयास करें।' : 'Verification failed. Please try again.';
};

export const PhoneAuth = ({ onBack, onSuccess }) => {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  
  const [step, setStep] = useState('phone'); // 'phone' | 'otp' | 'name'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [countdown, setCountdown] = useState(0);
  
  const otpRefs = useRef([]);
  const [recaptchaReady, setRecaptchaReady] = useState(true); // Assume ready, create on-demand

  // Countdown timer for resend OTP
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Limit to 10 digits
    return digits.slice(0, 10);
  };

  const sendOTP = async () => {
    if (phoneNumber.length !== 10) {
      setError(language === 'hi' ? 'कृपया 10 अंकों का मोबाइल नंबर दर्ज करें' : 'Please enter a 10-digit mobile number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Clean up any existing reCAPTCHA verifier
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {}
        window.recaptchaVerifier = null;
      }
      
      // Create fresh reCAPTCHA verifier on demand
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {
          console.log('reCAPTCHA verified');
        }
      });

      const fullNumber = `+91${phoneNumber}`;
      const confirmation = await signInWithPhoneNumber(auth, fullNumber, window.recaptchaVerifier);
      setConfirmationResult(confirmation);
      setStep('otp');
      setCountdown(30);
    } catch (err) {
      console.error('OTP send error:', err);
      
      // Reset reCAPTCHA on error
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {}
        window.recaptchaVerifier = null;
      }
      
      if (err.code === 'auth/too-many-requests') {
        setError(language === 'hi' ? 'बहुत अधिक प्रयास। कृपया बाद में पुनः प्रयास करें।' : 'Too many attempts. Please try again later.');
      } else if (err.code === 'auth/invalid-phone-number') {
        setError(language === 'hi' ? 'अमान्य फोन नंबर' : 'Invalid phone number');
      } else if (err.code === 'auth/argument-error') {
        setError(language === 'hi' 
          ? 'प्रीव्यू में उपलब्ध नहीं। प्रोडक्शन में काम करेगा।' 
          : 'Not available in preview. Will work in production.');
      } else {
        setError(language === 'hi' ? 'OTP भेजने में विफल। पुनः प्रयास करें।' : 'Failed to send OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError(language === 'hi' ? 'कृपया 6 अंकों का OTP दर्ज करें' : 'Please enter the 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await confirmationResult.confirm(otpCode);
      const user = result.user;
      const idToken = await user.getIdToken();
      
      // Send to backend to create/login user
      const response = await axios.post(`${API}/api/auth/phone-login`, {
        firebase_token: idToken,
        phone_number: `+91${phoneNumber}`
      }, { withCredentials: true });

      if (response.data.needs_name) {
        // New user - need to collect name
        setStep('name');
      } else {
        // Existing user - login successful
        loginWithToken(response.data.access_token, response.data.user);
        if (onSuccess) onSuccess();
        else navigate('/dashboard');
      }
    } catch (err) {
      console.error('OTP verify error:', err);
      setError(describeAuthError(err, language));
    } finally {
      setLoading(false);
    }
  };

  const completeSignup = async () => {
    if (!name.trim()) {
      setError(language === 'hi' ? 'कृपया अपना नाम दर्ज करें' : 'Please enter your name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const user = auth.currentUser;
      const idToken = await user.getIdToken();
      
      const response = await axios.post(`${API}/api/auth/phone-complete`, {
        firebase_token: idToken,
        phone_number: `+91${phoneNumber}`,
        name: name.trim()
      }, { withCredentials: true });

      loginWithToken(response.data.access_token, response.data.user);
      if (onSuccess) onSuccess();
      else navigate('/dashboard');
    } catch (err) {
      console.error('Signup complete error:', err);
      setError(describeAuthError(err, language));
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6);
      const newOtp = [...otp];
      digits.split('').forEach((digit, i) => {
        if (index + i < 6) newOtp[index + i] = digit;
      });
      setOtp(newOtp);
      const nextIndex = Math.min(index + digits.length, 5);
      otpRefs.current[nextIndex]?.focus();
    } else {
      const newOtp = [...otp];
      newOtp[index] = value.replace(/\D/g, '');
      setOtp(newOtp);
      if (value && index < 5) {
        otpRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const resendOTP = () => {
    if (countdown === 0) {
      setOtp(['', '', '', '', '', '']);
      // Reset reCAPTCHA before resending
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {}
        window.recaptchaVerifier = null;
      }
      // Go back to phone step to resend
      setStep('phone');
    }
  };

  return (
    <div className="w-full max-w-sm mx-auto">
      <AnimatePresence mode="wait">
        {step === 'phone' && (
          <motion.div
            key="phone"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Header */}
            <div className="text-center">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                isDark ? 'bg-[#FCE9D9]' : 'bg-[#FCE9D9]'
              }`}>
                <Phone className="w-8 h-8 text-[#C84B32]" />
              </div>
              <h2 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                {language === 'hi' ? 'फोन से लॉगिन करें' : 'Login with Phone'}
              </h2>
              <p className={`text-sm ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                {language === 'hi' ? 'हम आपके नंबर पर OTP भेजेंगे' : "We'll send an OTP to your number"}
              </p>
            </div>

            {/* Phone Input */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-[#4A4640]' : 'text-[#4A4640]'}`}>
                {language === 'hi' ? 'मोबाइल नंबर' : 'Mobile Number'}
              </label>
              <div className="flex gap-2">
                <div className={`flex items-center px-3 rounded-lg border ${
                  isDark ? 'bg-[#FCE9D9]/40 border-[#282622]/10 text-white' : 'bg-[#FFF9EF] border-[#282622]/10 text-[#282622]'
                }`}>
                  <span>🇮🇳</span>
                  <span className="ml-1">+91</span>
                </div>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                  placeholder="9876543210"
                  className={`flex-1 min-w-0 w-0 px-4 py-3 rounded-lg border text-lg tracking-wider ${
                    isDark 
                      ? 'bg-[#FCE9D9]/40 border-[#282622]/10 text-white placeholder-gray-500' 
                      : 'bg-white border-[#282622]/10 text-[#282622] placeholder-gray-400'
                  } focus:outline-none focus:ring-2 focus:ring-[#C84B32]/30`}
                  maxLength={10}
                  data-testid="phone-input"
                />
              </div>
            </div>

            {/* Error */}
            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}

            {/* reCAPTCHA Container */}
            <div id="recaptcha-container" className="flex justify-center"></div>

            {/* Send OTP Button */}
            <Button
              id="send-otp-button"
              onClick={sendOTP}
              disabled={loading || phoneNumber.length !== 10}
              className="w-full py-3 bg-gradient-to-r from-[#C84B32] to-[#E85D43] text-white rounded-full font-semibold"
              data-testid="send-otp-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              ) : (
                language === 'hi' ? 'OTP भेजें' : 'Send OTP'
              )}
            </Button>

            {/* Back button */}
            {onBack && (
              <button
                onClick={onBack}
                className={`w-full flex items-center justify-center gap-2 py-2 text-sm ${
                  isDark ? 'text-[#6B6560] hover:text-white' : 'text-[#6B6560] hover:text-[#4A4640]'
                }`}
              >
                <ArrowLeft className="w-4 h-4" />
                {language === 'hi' ? 'वापस जाएं' : 'Go back'}
              </button>
            )}
          </motion.div>
        )}

        {step === 'otp' && (
          <motion.div
            key="otp"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Header */}
            <div className="text-center">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                isDark ? 'bg-blue-500/20' : 'bg-blue-100'
              }`}>
                <Shield className="w-8 h-8 text-blue-500" />
              </div>
              <h2 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                {language === 'hi' ? 'OTP दर्ज करें' : 'Enter OTP'}
              </h2>
              <p className={`text-sm ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                {language === 'hi' ? `+91 ${phoneNumber} पर भेजा गया` : `Sent to +91 ${phoneNumber}`}
              </p>
            </div>

            {/* OTP Input */}
            <div className="flex justify-center gap-2">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (otpRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className={`w-10 sm:w-12 h-14 text-center text-xl font-bold rounded-lg border ${
                    isDark 
                      ? 'bg-[#FCE9D9]/40 border-[#282622]/10 text-white' 
                      : 'bg-white border-[#282622]/10 text-[#282622]'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
                  maxLength={1}
                  data-testid={`otp-input-${index}`}
                />
              ))}
            </div>

            {/* Resend OTP */}
            <div className="text-center">
              {countdown > 0 ? (
                <p className={`text-sm ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                  {language === 'hi' ? `${countdown} सेकंड में पुनः भेजें` : `Resend in ${countdown}s`}
                </p>
              ) : (
                <button
                  onClick={resendOTP}
                  className="text-sm text-blue-500 hover:text-blue-600"
                >
                  {language === 'hi' ? 'OTP पुनः भेजें' : 'Resend OTP'}
                </button>
              )}
            </div>

            {/* Error */}
            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}

            {/* Verify Button */}
            <Button
              onClick={verifyOTP}
              disabled={loading || otp.join('').length !== 6}
              className="w-full py-3 bg-gradient-to-r from-[#C84B32] to-[#E85D43] text-white rounded-lg font-medium"
              data-testid="verify-otp-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              ) : (
                language === 'hi' ? 'सत्यापित करें' : 'Verify OTP'
              )}
            </Button>

            {/* Change number */}
            <button
              onClick={() => {
                setStep('phone');
                setOtp(['', '', '', '', '', '']);
                setError('');
              }}
              className={`w-full flex items-center justify-center gap-2 py-2 text-sm ${
                isDark ? 'text-[#6B6560] hover:text-white' : 'text-[#6B6560] hover:text-[#4A4640]'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              {language === 'hi' ? 'नंबर बदलें' : 'Change number'}
            </button>
          </motion.div>
        )}

        {step === 'name' && (
          <motion.div
            key="name"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Header */}
            <div className="text-center">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                isDark ? 'bg-[#FCE9D9]' : 'bg-[#FCE9D9]'
              }`}>
                <CheckCircle className="w-8 h-8 text-[#C84B32]" />
              </div>
              <h2 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                {language === 'hi' ? 'आपका स्वागत है!' : 'Welcome!'}
              </h2>
              <p className={`text-sm ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                {language === 'hi' ? 'कृपया अपना नाम बताएं' : 'Please tell us your name'}
              </p>
            </div>

            {/* Name Input */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-[#4A4640]' : 'text-[#4A4640]'}`}>
                {language === 'hi' ? 'पूरा नाम' : 'Full Name'}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={language === 'hi' ? 'अपना नाम दर्ज करें' : 'Enter your name'}
                className={`w-full px-4 py-3 rounded-lg border ${
                  isDark 
                    ? 'bg-[#FCE9D9]/40 border-[#282622]/10 text-white placeholder-gray-500' 
                    : 'bg-white border-[#282622]/10 text-[#282622] placeholder-gray-400'
                } focus:outline-none focus:ring-2 focus:ring-[#C84B32]/30`}
                data-testid="name-input"
              />
            </div>

            {/* Error */}
            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}

            {/* Complete Button */}
            <Button
              onClick={completeSignup}
              disabled={loading || !name.trim()}
              className="w-full py-3 bg-gradient-to-r from-[#C84B32] to-[#E85D43] text-white rounded-lg font-medium"
              data-testid="complete-signup-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              ) : (
                language === 'hi' ? 'शुरू करें' : 'Get Started'
              )}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PhoneAuth;
